'use client';

import { FormProvider } from 'react-hook-form';
import { MultistepForm } from '../../../../../form-controls/MultistepForm/MultistepForm';
import { HiddenTrigger, MyAccountSheet } from '../../../../MyAccountSheet/MyAccountSheet';

import { API_ROUTES } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { phoneNumberForInput, phoneNumberForVerification } from '../../../../../form-controls/PhoneNumberInput/phoneNumberUtils';
import { toast } from '../../../../../Toast/Toast';
import { useStepTracking } from '../../../../hooks/useStepTracking';
import { useVerificationApiMutation } from '../../../hooks/useVerificationApiMutation';
import { invalidateMyAccountVerificationQueries } from '../../../utils/invalidateMyAccountVerificationQueries';
import { clearMobileOtpResendLimiters } from '../../../utils/otpResendLimiterStorageKey';
import { isVerificationRateLimitError } from '../../../utils/verificationErrorUtils';
import { createEditMobileNumberSteps } from '../config/EditMobileNumberSteps';
import { useEditMobileNumber } from '../hooks/UseEditMobileNumber';
import type { EditMobileNumberInitialFlow, SendVerificationPayload, SendVerificationResponse } from '../types/edit-mobile-number';

/** Matches player profile GET: ISO country, dial digits (no +), national mobile digits — same strings `PhoneNumberInput` uses elsewhere (`${country}+${countryCode}`). */
type EditMobileNumberSheetUser = {
  email?: string;
  country?: string;
  countryCode?: string;
  mobilePhoneNumber?: string;
  /** Fallback when `mobilePhoneNumber` / `countryCode` are not loaded yet (e.g. SSR); passed through to `phoneNumberForVerification` */
  mobileNumber?: string;
};

type EditMobileNumberSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: EditMobileNumberSheetUser;
  namespace?: string;
  initialFlow?: EditMobileNumberInitialFlow;
};

function buildInitialMobileNumberValue(user: EditMobileNumberSheetUser, isAddMode: boolean) {
  if (isAddMode) {
    return {
      prefix: getSiteConfig().PHONE_NUMBER.defaultPrefix,
      number: '',
    };
  }

  return phoneNumberForInput(user.country, user.countryCode, user.mobilePhoneNumber);
}

const EditMobileNumberSheet = ({ open, onOpenChange, user, namespace, initialFlow = 'edit' }: EditMobileNumberSheetProps) => {
  const t = useTranslations(namespace);
  const tOtp = useTranslations('form.otp');
  const isAddFlow = initialFlow === 'add';
  const isVerifyFlow = initialFlow === 'verify';
  const flowMode = isAddFlow ? 'add' : 'edit';
  const sheetTitle = isAddFlow ? t('add_title') : t('title');
  const shortHeaderTitle = t('short_title');

  const { formMethods, handleBackNavigation, registerBackHandler } = useEditMobileNumber();
  const [showBackButton, setShowBackButton] = useState(false);
  const queryClient = useQueryClient();
  const { update: updateSession } = useSession();

  const skipToStepRef = useRef<((stepId: string) => void) | null>(null);
  const setStatusRef = useRef<((status: string) => void) | null>(null);

  const { mutateAsync: sendVerification } = useVerificationApiMutation<SendVerificationResponse, SendVerificationPayload>(API_ROUTES.MY_ACCOUNT.VERIFICATION.SEND);

  useEffect(() => {
    if (!open) return;

    setStatusRef.current?.('');
    clearMobileOtpResendLimiters();

    const phoneForVerification = phoneNumberForVerification(user);
    const initialMobileNumber = buildInitialMobileNumberValue(user, isAddFlow);

    formMethods.reset({
      mobileNumber: initialMobileNumber,
      verificationCode: '',
      verificationId: '',
    });

    if (isVerifyFlow && phoneForVerification) {
      sendVerification({ method: 'phone', value: phoneForVerification })
        .then(response => {
          if (response) formMethods.setValue('verificationId', response.verificationId);
        })
        .catch(error => {
          if (isVerificationRateLimitError(error)) {
            toast(tOtp('maxAttemptsReached'), { type: 'error' });
            return;
          }
          toast(t('steps.edit_mobile_number.error'), { type: 'error' });
        });
    }
  }, [open]);

  const handleClose = () => {
    setStatusRef.current?.('');
    formMethods.reset({
      mobileNumber: buildInitialMobileNumberValue(user, isAddFlow),
      verificationCode: '',
      verificationId: '',
    });
    setShowBackButton(false);
    onOpenChange(false);
  };

  const handleComplete = () => {
    invalidateMyAccountVerificationQueries(queryClient);
    void updateSession();
    handleClose();
  };

  const handleMultistepFormReady = useCallback(
    (skipToStep: (stepId: string) => void, setStatus: (status: string) => void) => {
      skipToStepRef.current = skipToStep;
      setStatusRef.current = setStatus;
      setStatus('');
      if (isVerifyFlow) {
        skipToStep('verification');
      }
    },
    [isVerifyFlow]
  );

  const handleOpenAutoFocus = useCallback(
    (event: Event) => {
      if (!isVerifyFlow) {
        event.preventDefault();
      }
    },
    [isVerifyFlow]
  );

  const editMobileNumberSteps = createEditMobileNumberSteps(
    registerBackHandler,
    t,
    handleComplete,
    handleClose,
    flowMode,
    isAddFlow ? undefined : phoneNumberForVerification(user) || undefined
  );
  const { currentTrackContainer, onStepChange } = useStepTracking(editMobileNumberSteps);

  const handleStepChange = (isFirstStep: boolean, isLastStep: boolean, stepId: string) => {
    onStepChange(isFirstStep, isLastStep, stepId);

    if (isVerifyFlow) {
      setShowBackButton(false);
      return;
    }
    setShowBackButton(!(isFirstStep || isLastStep));
  };

  return (
    <MyAccountSheet
      trigger={<HiddenTrigger label={sheetTitle} />}
      title={shortHeaderTitle}
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
      showBackButton={showBackButton}
      onBack={() => handleBackNavigation(handleClose)}
      onOpenAutoFocus={handleOpenAutoFocus}
      data-track-feature="My Account"
      data-track-container={currentTrackContainer}
    >
      <FormProvider {...formMethods}>
        <MultistepForm
          steps={editMobileNumberSteps}
          onMultistepFormReady={handleMultistepFormReady}
          onStepChange={handleStepChange}
          showProgressBar={false}
          showNavigation={false}
          onComplete={handleComplete}
          className="h-full px-6 sm:px-10 pt-8 pb-6"
        />
      </FormProvider>
    </MyAccountSheet>
  );
};

export { EditMobileNumberSheet };
